import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useToast } from './ToastContext';
import { get, post, BASE_URL } from '../utils/api';
import { Container, Table, Spinner, Button, Modal, Form, Image, Badge, InputGroup, Row, Col, Alert } from 'react-bootstrap';
import { 
  FaFilePrescription, FaSearch, FaSyncAlt, FaEye, FaCheckCircle, 
  FaTimesCircle, FaClock, FaCalendarAlt, FaUser, FaSearchPlus, 
  FaSearchMinus, FaRedo, FaDownload, FaThLarge, FaList, FaFilter,
  FaCheck, FaTimes, FaShieldAlt, FaExclamationTriangle, FaCommentAlt
} from 'react-icons/fa';
import './PrescriptionManagement.css';
import ProgressDialog from './ProgressDialog';
import NoDataCard from './NoDataCard';

const REJECTION_PRESETS = [
  "Blurry or unreadable document image",
  "Prescription has expired",
  "Doctor signature, seal or license missing",
  "Patient name does not match order",
  "Incomplete prescription / dosage missing",
  "Restricted / Schedule X drug requires physical original"
];

const PrescriptionManagement = () => {
  const { showToast } = useToast();
  const [prescriptionList, setPrescriptionList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Modals & Image Viewer
  const [showModal, setShowModal] = useState(false);
  const [currentPrescription, setCurrentPrescription] = useState(null);
  const [newStatus, setNewStatus] = useState('');
  const [remarks, setRemarks] = useState('');
  const [remarksError, setRemarksError] = useState('');
  const [updatingStatus, setUpdatingStatus] = useState(false);

  const [showImageModal, setShowImageModal] = useState(false);
  const [selectedImageUrl, setSelectedImageUrl] = useState('');
  const [zoom, setZoom] = useState(1);
  const [rotation, setRotation] = useState(0);

  // Search, Filter & View
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [viewMode, setViewMode] = useState('grid'); // 'grid' | 'table'
  const [sortBy, setSortBy] = useState('newest');

  const fetchPrescriptions = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await get('/prescriptions');
      if (response && response.success && Array.isArray(response.data)) {
        setPrescriptionList(response.data);
      } else if (response && Array.isArray(response.data)) {
        setPrescriptionList(response.data);
      } else {
        throw new Error(response?.message || 'Failed to fetch prescriptions.');
      }
    } catch (err) {
      setError(err.message || 'An error occurred while loading prescriptions.');
      setPrescriptionList([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPrescriptions();
  }, [fetchPrescriptions]);

  const handleShowImageModal = (imageUrl) => {
    setSelectedImageUrl(imageUrl);
    setZoom(1);
    setRotation(0);
    setShowImageModal(true);
  };

  const handleCloseImageModal = () => setShowImageModal(false);
  const handleZoomIn = () => setZoom(prev => Math.min(3, prev + 0.25));
  const handleZoomOut = () => setZoom(prev => Math.max(0.5, prev - 0.25));
  const handleRotate = () => setRotation(prev => (prev + 90) % 360);
  const handleResetZoom = () => { setZoom(1); setRotation(0); };

  const handleShowModal = (prescription, defaultStatus = null) => {
    setCurrentPrescription(prescription);
    setNewStatus(defaultStatus || prescription.status || 'pending_verification');
    setRemarks(prescription.remarks || '');
    setRemarksError('');
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setCurrentPrescription(null);
    setRemarks('');
    setRemarksError('');
  };

  const handleQuickApprove = async (prescriptionId) => {
    try {
      setLoading(true);
      const result = await post(`/prescriptions/${prescriptionId}/status`, { 
        status: 'approved',
        remarks: 'Approved by pharmacist'
      });
      if (result && result.success) {
        showToast('Prescription approved successfully!', 'success');
        fetchPrescriptions();
      } else {
        showToast(result?.message || 'Failed to approve prescription', 'error');
      }
    } catch (err) {
      showToast(err.message || 'Error updating status', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleQuickRejectClick = (prescription) => {
    // Open verification modal with "rejected" preselected and prompt for mandatory remarks
    handleShowModal(prescription, 'rejected');
  };

  const handleSubmitStatusUpdate = async (event) => {
    event.preventDefault();
    if (!currentPrescription) return;

    // Validate mandatory rejection reason
    if (newStatus === 'rejected') {
      if (!remarks || remarks.trim() === '') {
        setRemarksError('Rejection reason / remarks are mandatory when declining a prescription.');
        showToast('Please provide a reason for rejecting this prescription.', 'error');
        return;
      }
    }

    setUpdatingStatus(true);
    setRemarksError('');
    try {
      const result = await post(`/prescriptions/${currentPrescription.prescription_id}/status`, { 
        status: newStatus,
        remarks: remarks.trim()
      });
      if (result && result.success) {
        showToast(`Prescription marked as ${newStatus.replace(/_/g, ' ')}!`, 'success');
        handleCloseModal();
        fetchPrescriptions();
      } else {
        showToast(result?.message || 'Operation failed.', 'error');
      }
    } catch (err) {
      showToast(err.message || 'Operation failed.', 'error');
    } finally {
      setUpdatingStatus(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const d = new Date(dateString);
    return !isNaN(d)
      ? d.toLocaleDateString('en-US', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })
      : 'N/A';
  };

  const getStatusBadge = (status) => {
    const s = String(status || '').toLowerCase();
    switch (s) {
      case 'approved':
        return <span className="rx-status-pill rx-approved"><FaCheckCircle className="me-1" /> Approved</span>;
      case 'rejected':
        return <span className="rx-status-pill rx-rejected"><FaTimesCircle className="me-1" /> Rejected</span>;
      default:
        return <span className="rx-status-pill rx-pending"><FaClock className="me-1" /> Pending Review</span>;
    }
  };

  // Metrics Calculation
  const metrics = useMemo(() => {
    const total = prescriptionList.length;
    const pending = prescriptionList.filter(p => (p.status || '').toLowerCase() === 'pending_verification' || (p.status || '').toLowerCase() === 'pending').length;
    const approved = prescriptionList.filter(p => (p.status || '').toLowerCase() === 'approved').length;
    const rejected = prescriptionList.filter(p => (p.status || '').toLowerCase() === 'rejected').length;
    return { total, pending, approved, rejected };
  }, [prescriptionList]);

  // Filtered & Sorted Prescriptions
  const displayedPrescriptions = useMemo(() => {
    return prescriptionList
      .filter((item) => {
        if (statusFilter !== 'all') {
          const itemStatus = (item.status || 'pending_verification').toLowerCase();
          if (statusFilter === 'pending' && itemStatus !== 'pending_verification' && itemStatus !== 'pending') return false;
          if (statusFilter === 'approved' && itemStatus !== 'approved') return false;
          if (statusFilter === 'rejected' && itemStatus !== 'rejected') return false;
        }

        if (searchTerm.trim() !== '') {
          const q = searchTerm.toLowerCase();
          const matchCustomer = (item.customer_name || '').toLowerCase().includes(q);
          const matchId = String(item.prescription_id || '').includes(q);
          const matchCustomerId = String(item.customer_id || '').includes(q);
          const matchRemarks = String(item.remarks || '').toLowerCase().includes(q);
          if (!matchCustomer && !matchId && !matchCustomerId && !matchRemarks) return false;
        }

        return true;
      })
      .sort((a, b) => {
        if (sortBy === 'newest') return new Date(b.upload_date) - new Date(a.upload_date);
        if (sortBy === 'oldest') return new Date(a.upload_date) - new Date(b.upload_date);
        if (sortBy === 'customer') return (a.customer_name || '').localeCompare(b.customer_name || '');
        return 0;
      });
  }, [prescriptionList, statusFilter, searchTerm, sortBy]);

  const getFullImageUrl = (imagePath) => {
    if (!imagePath) return '';
    if (imagePath.startsWith('http')) return imagePath;
    const cleanPath = imagePath.startsWith('/') ? imagePath : `/${imagePath}`;
    return `${BASE_URL}${cleanPath}`;
  };

  return (
    <Container fluid className="rx-mgmt-page px-3 px-lg-4 py-3">
      <ProgressDialog show={loading && prescriptionList.length === 0} message="Loading prescriptions..." />

      {/* Header & KPI Summary Cards */}
      <div className="rx-header-panel mb-4 p-3 p-lg-4 rounded glassmorphic-panel">
        <div className="d-flex justify-content-between align-items-center flex-wrap gap-3 mb-3">
          <div className="d-flex align-items-center gap-3">
            <div className="rx-master-icon">
              <FaFilePrescription />
            </div>
            <div>
              <h3 className="rx-title mb-0">Prescription Management</h3>
              <p className="rx-subtitle mb-0 text-muted">Verify uploaded doctor prescriptions, authorize medicines & track review compliance.</p>
            </div>
          </div>

          <Button 
            variant="outline-secondary" 
            size="sm" 
            onClick={fetchPrescriptions} 
            className="rx-refresh-btn d-flex align-items-center gap-2"
          >
            <FaSyncAlt className={loading ? 'fa-spin' : ''} /> Refresh Queue
          </Button>
        </div>

        {/* KPI Metrics Row */}
        <Row className="g-3">
          <Col xs={6} md={3}>
            <div className="rx-kpi-card">
              <div className="kpi-icon total">
                <FaFilePrescription />
              </div>
              <div className="kpi-content">
                <div className="kpi-value">{metrics.total}</div>
                <div className="kpi-label">Total Uploads</div>
              </div>
            </div>
          </Col>
          <Col xs={6} md={3}>
            <div className="rx-kpi-card">
              <div className="kpi-icon pending">
                <FaClock />
              </div>
              <div className="kpi-content">
                <div className="kpi-value text-warning">{metrics.pending}</div>
                <div className="kpi-label">Pending Review</div>
              </div>
            </div>
          </Col>
          <Col xs={6} md={3}>
            <div className="rx-kpi-card">
              <div className="kpi-icon approved">
                <FaCheckCircle />
              </div>
              <div className="kpi-content">
                <div className="kpi-value text-success">{metrics.approved}</div>
                <div className="kpi-label">Approved</div>
              </div>
            </div>
          </Col>
          <Col xs={6} md={3}>
            <div className="rx-kpi-card">
              <div className="kpi-icon rejected">
                <FaTimesCircle />
              </div>
              <div className="kpi-content">
                <div className="kpi-value text-danger">{metrics.rejected}</div>
                <div className="kpi-label">Rejected</div>
              </div>
            </div>
          </Col>
        </Row>
      </div>

      {/* Filter & Search Toolbar */}
      <div className="rx-toolbar-panel mb-4 p-3 rounded glassmorphic-panel">
        <div className="d-flex align-items-center justify-content-between flex-wrap gap-3">
          {/* Search Box */}
          <div className="rx-search-wrapper flex-grow-1">
            <InputGroup className="rx-search-group">
              <InputGroup.Text className="bg-transparent border-0 pe-1 text-muted">
                <FaSearch />
              </InputGroup.Text>
              <Form.Control
                type="text"
                placeholder="Search customer, Rx ID, customer ID, rejection reason..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="rx-search-input border-0 ps-1"
              />
              {searchTerm && (
                <Button 
                  variant="link" 
                  onClick={() => setSearchTerm('')} 
                  className="rx-clear-search text-muted border-0"
                >
                  <FaTimes />
                </Button>
              )}
            </InputGroup>
          </div>

          {/* Status Filter Chips */}
          <div className="rx-status-chips d-flex align-items-center gap-2 flex-wrap">
            <button 
              type="button"
              className={`rx-filter-chip ${statusFilter === 'all' ? 'active' : ''}`}
              onClick={() => setStatusFilter('all')}
            >
              All ({metrics.total})
            </button>
            <button 
              type="button"
              className={`rx-filter-chip pending ${statusFilter === 'pending' ? 'active' : ''}`}
              onClick={() => setStatusFilter('pending')}
            >
              Pending ({metrics.pending})
            </button>
            <button 
              type="button"
              className={`rx-filter-chip approved ${statusFilter === 'approved' ? 'active' : ''}`}
              onClick={() => setStatusFilter('approved')}
            >
              Approved ({metrics.approved})
            </button>
            <button 
              type="button"
              className={`rx-filter-chip rejected ${statusFilter === 'rejected' ? 'active' : ''}`}
              onClick={() => setStatusFilter('rejected')}
            >
              Rejected ({metrics.rejected})
            </button>
          </div>

          {/* Controls Cluster */}
          <div className="d-flex align-items-center gap-2">
            <Form.Select 
              value={sortBy} 
              onChange={(e) => setSortBy(e.target.value)}
              className="rx-select-pill"
              size="sm"
            >
              <option value="newest">📅 Newest Uploads</option>
              <option value="oldest">📅 Oldest Uploads</option>
              <option value="customer">👤 Customer Name (A-Z)</option>
            </Form.Select>

            <div className="rx-view-switch">
              <button 
                type="button" 
                className={`switch-btn ${viewMode === 'grid' ? 'active' : ''}`}
                onClick={() => setViewMode('grid')}
                title="Card Grid View"
              >
                <FaThLarge />
              </button>
              <button 
                type="button" 
                className={`switch-btn ${viewMode === 'table' ? 'active' : ''}`}
                onClick={() => setViewMode('table')}
                title="Table List View"
              >
                <FaList />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content Area */}
      {displayedPrescriptions.length === 0 && !loading ? (
        <div className="rx-no-data-box py-5 text-center rounded-4 glassmorphic-panel">
          <NoDataCard 
            className="no-data-inline border-0 shadow-none bg-transparent"
            title="No Prescriptions Found"
            message={searchTerm || statusFilter !== 'all' ? "No prescriptions match your current search query or status filter criteria." : "There are no customer prescriptions uploaded to the system yet."}
            icon="🧾"
            actionButton={
              (searchTerm || statusFilter !== 'all') && (
                <Button 
                  variant="primary" 
                  size="sm" 
                  onClick={() => { setSearchTerm(''); setStatusFilter('all'); }}
                  className="rx-refresh-btn shadow-sm"
                >
                  <FaRedo className="me-1" /> Reset Search & Filters
                </Button>
              )
            }
          />
          <div className="rx-empty-hint-badge">
            <FaShieldAlt /> <span>Tip: Customer prescription uploads from the mobile app or POS counter will appear here for verification.</span>
          </div>
        </div>
      ) : viewMode === 'grid' ? (
        /* Visual Cards Grid */
        <div className="rx-cards-grid">
          {displayedPrescriptions.map((prescription, idx) => {
            const imgUrl = getFullImageUrl(prescription.image_url);
            const isPending = (prescription.status || '').toLowerCase() === 'pending_verification' || (prescription.status || '').toLowerCase() === 'pending';
            const isRejected = (prescription.status || '').toLowerCase() === 'rejected';

            return (
              <div className="rx-item-card glassmorphic-panel" key={prescription.prescription_id || idx}>
                {/* Thumbnail Preview Area */}
                <div className="rx-thumbnail-wrapper" onClick={() => handleShowImageModal(imgUrl)}>
                  {imgUrl ? (
                    <Image src={imgUrl} alt="Prescription Document" className="rx-thumbnail-img" />
                  ) : (
                    <div className="rx-thumbnail-placeholder">
                      <FaFilePrescription />
                      <span>No Preview Available</span>
                    </div>
                  )}
                  <div className="rx-thumbnail-overlay">
                    <FaEye className="me-1" /> Click to Inspect & Zoom
                  </div>
                  <div className="rx-index-badge">
                    #{idx + 1}
                  </div>
                </div>

                {/* Card Content Details */}
                <div className="rx-card-details p-3">
                  <div className="d-flex justify-content-between align-items-start mb-2 gap-2">
                    <div>
                      <h6 className="rx-customer-name mb-0 text-truncate" title={prescription.customer_name || 'Anonymous Customer'}>
                        <FaUser className="text-muted me-1 small" /> {prescription.customer_name || 'Customer #' + prescription.customer_id}
                      </h6>
                      <span className="rx-id-tag">Rx ID: #{prescription.prescription_id}</span>
                    </div>
                    <div>
                      {getStatusBadge(prescription.status)}
                    </div>
                  </div>

                  <div className="rx-meta-row mb-2 d-flex align-items-center justify-content-between small text-muted">
                    <span className="d-flex align-items-center gap-1">
                      <FaCalendarAlt /> {formatDate(prescription.upload_date)}
                    </span>
                  </div>

                  {/* Rejection Remarks Callout if Rejected */}
                  {isRejected && prescription.remarks && (
                    <div className="rx-rejection-callout mb-2 p-2 rounded small">
                      <strong className="text-danger d-block mb-1">
                        <FaExclamationTriangle className="me-1" /> Rejection Reason:
                      </strong>
                      <span className="rx-rejection-text">{prescription.remarks}</span>
                    </div>
                  )}

                  {/* Actions Footer */}
                  <div className="rx-card-actions pt-2 border-top d-flex gap-2">
                    <Button 
                      variant="outline-primary" 
                      size="sm" 
                      onClick={() => handleShowModal(prescription)}
                      className="flex-grow-1 rx-status-btn d-flex align-items-center justify-content-center gap-1"
                    >
                      <FaShieldAlt /> Status
                    </Button>
                    {isPending && (
                      <Button 
                        variant="success" 
                        size="sm" 
                        onClick={() => handleQuickApprove(prescription.prescription_id)}
                        className="rx-quick-action-btn"
                        title="Quick Approve"
                      >
                        <FaCheck />
                      </Button>
                    )}
                    {isPending && (
                      <Button 
                        variant="danger" 
                        size="sm" 
                        onClick={() => handleQuickRejectClick(prescription)}
                        className="rx-quick-action-btn"
                        title="Decline with Remarks"
                      >
                        <FaTimes />
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        /* High Density Table View */
        <div className="rx-table-wrapper rounded glassmorphic-panel overflow-hidden">
          <Table responsive hover className="rx-data-table mb-0 align-middle">
            <thead>
              <tr>
                <th style={{ width: '60px' }}>#</th>
                <th style={{ width: '80px' }}>Preview</th>
                <th>Rx ID</th>
                <th>Customer Name</th>
                <th>Upload Date</th>
                <th>Status & Remarks</th>
                <th className="text-end">Verification Actions</th>
              </tr>
            </thead>
            <tbody>
              {displayedPrescriptions.map((prescription, idx) => {
                const imgUrl = getFullImageUrl(prescription.image_url);
                const isPending = (prescription.status || '').toLowerCase() === 'pending_verification' || (prescription.status || '').toLowerCase() === 'pending';
                const isRejected = (prescription.status || '').toLowerCase() === 'rejected';

                return (
                  <tr key={prescription.prescription_id || idx}>
                    <td className="text-muted fw-bold">#{idx + 1}</td>
                    <td>
                      <div className="rx-mini-thumb" onClick={() => handleShowImageModal(imgUrl)}>
                        {imgUrl ? (
                          <Image src={imgUrl} alt="Rx preview" className="rx-mini-img" />
                        ) : (
                          <FaFilePrescription className="text-muted" />
                        )}
                      </div>
                    </td>
                    <td>
                      <span className="rx-id-pill">#{prescription.prescription_id}</span>
                    </td>
                    <td>
                      <div className="fw-semibold text-white">{prescription.customer_name || 'Customer #' + prescription.customer_id}</div>
                      <div className="small text-muted">ID: {prescription.customer_id || 'N/A'}</div>
                    </td>
                    <td className="small text-muted">{formatDate(prescription.upload_date)}</td>
                    <td>
                      <div>{getStatusBadge(prescription.status)}</div>
                      {isRejected && prescription.remarks && (
                        <div className="rx-table-remarks text-danger small mt-1">
                          <strong>Reason:</strong> {prescription.remarks}
                        </div>
                      )}
                    </td>
                    <td className="text-end">
                      <div className="d-flex align-items-center justify-content-end gap-2">
                        <Button 
                          variant="outline-light" 
                          size="sm" 
                          onClick={() => handleShowImageModal(imgUrl)}
                          className="rx-table-btn"
                          title="View Fullscreen"
                        >
                          <FaEye />
                        </Button>
                        <Button 
                          variant="outline-primary" 
                          size="sm" 
                          onClick={() => handleShowModal(prescription)}
                          className="rx-table-btn"
                        >
                          Update Status
                        </Button>
                        {isPending && (
                          <Button 
                            variant="success" 
                            size="sm" 
                            onClick={() => handleQuickApprove(prescription.prescription_id)}
                            className="rx-table-btn text-white"
                            title="Approve"
                          >
                            <FaCheck />
                          </Button>
                        )}
                        {isPending && (
                          <Button 
                            variant="danger" 
                            size="sm" 
                            onClick={() => handleQuickRejectClick(prescription)}
                            className="rx-table-btn text-white"
                            title="Decline with Remarks"
                          >
                            <FaTimes />
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </Table>
        </div>
      )}

      {/* Update Status Modal with Mandatory Remarks Validation */}
      <Modal 
        show={showModal} 
        onHide={handleCloseModal} 
        scrollable
        className="rx-modal-modern"
      >
        <Modal.Header closeButton className="rx-modal-header py-3 px-4">
          <div className="d-flex align-items-center gap-3">
            <div className="rx-modal-icon">
              <FaShieldAlt />
            </div>
            <div>
              <Modal.Title className="rx-modal-title mb-0">Update Prescription Status</Modal.Title>
              <div className="rx-modal-subtitle">
                Prescription <span className="fw-bold text-primary">#{currentPrescription?.prescription_id}</span> • <span className="fw-semibold">{currentPrescription?.customer_name || 'Customer #' + currentPrescription?.customer_id}</span>
              </div>
            </div>
          </div>
        </Modal.Header>

        <Modal.Body className="rx-modal-body p-4">
          {currentPrescription && (
            <div>
              {/* Document Thumbnail Preview */}
              <div 
                className="rx-modal-preview-box text-center mb-3 rounded p-2" 
                onClick={() => handleShowImageModal(getFullImageUrl(currentPrescription.image_url))}
                title="Click to view full image"
              >
                <Image 
                  src={getFullImageUrl(currentPrescription.image_url)} 
                  fluid 
                  className="rx-modal-img rounded"
                  alt="Prescription Document"
                />
                <div className="rx-modal-preview-hint mt-2">
                  <FaEye className="me-1 text-primary" /> Click to open fullscreen zoom & rotate viewer
                </div>
              </div>

              <Form onSubmit={handleSubmitStatusUpdate}>
                <Form.Group className="mb-3">
                  <Form.Label className="rx-modal-label">Select Verification Status</Form.Label>
                  <Form.Select 
                    value={newStatus} 
                    onChange={(e) => {
                      setNewStatus(e.target.value);
                      setRemarksError('');
                    }}
                    className="rx-status-select"
                  >
                    <option value="pending_verification">⏳ Pending Review</option>
                    <option value="approved">✅ Approved (Valid Prescription)</option>
                    <option value="rejected">❌ Rejected (Decline Prescription)</option>
                  </Form.Select>
                </Form.Group>

                {/* Mandatory Rejection Remarks Section */}
                {newStatus === 'rejected' && (
                  <div className="rx-rejection-field-group mb-3 p-3 rounded">
                    <div className="d-flex justify-content-between align-items-center mb-2 flex-wrap gap-1">
                      <Form.Label className="rx-rejection-label mb-0">
                        <FaExclamationTriangle className="me-1" /> Rejection Reason <span className="text-danger">*</span> (Mandatory)
                      </Form.Label>
                      <span className="rx-preset-hint">Click preset or type custom reason:</span>
                    </div>

                    {/* Quick Preset Buttons */}
                    <div className="rx-preset-chips mb-2 d-flex flex-wrap gap-1">
                      {REJECTION_PRESETS.map((preset, pIdx) => (
                        <button
                          key={pIdx}
                          type="button"
                          className="rx-preset-btn"
                          onClick={() => {
                            setRemarks(preset);
                            setRemarksError('');
                          }}
                        >
                          {preset}
                        </button>
                      ))}
                    </div>

                    <Form.Control
                      as="textarea"
                      rows={3}
                      placeholder="Enter the specific reason why this prescription is declined (e.g. illegible doctor handwriting, expired, missing seal)..."
                      value={remarks}
                      onChange={(e) => {
                        setRemarks(e.target.value);
                        if (e.target.value.trim()) setRemarksError('');
                      }}
                      className={`rx-remarks-textarea ${remarksError ? 'is-invalid' : ''}`}
                      required
                    />

                    {remarksError && (
                      <div className="rx-error-message mt-1">
                        ⚠️ {remarksError}
                      </div>
                    )}
                  </div>
                )}

                {/* Optional Note for Approved Status */}
                {newStatus === 'approved' && (
                  <Form.Group className="mb-3">
                    <Form.Label className="rx-modal-label">
                      Verification Notes / Remarks <span className="rx-optional-hint">(Optional)</span>
                    </Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="e.g. Verified dosage with Dr. Sharma / Original shown"
                      value={remarks}
                      onChange={(e) => setRemarks(e.target.value)}
                      className="rx-status-input"
                    />
                  </Form.Group>
                )}

                <div className="d-flex justify-content-end gap-2 pt-3 border-top rx-modal-footer-row">
                  <Button variant="secondary" onClick={handleCloseModal} disabled={updatingStatus} className="rx-modal-cancel-btn">
                    Cancel
                  </Button>
                  <Button 
                    variant={newStatus === 'rejected' ? 'danger' : 'primary'} 
                    type="submit" 
                    disabled={updatingStatus} 
                    className="rx-modal-submit-btn fw-bold px-4"
                  >
                    {updatingStatus ? (
                      <><Spinner animation="border" size="sm" className="me-1" /> Saving...</>
                    ) : (
                      newStatus === 'rejected' ? 'Confirm Rejection' : 'Save Verification'
                    )}
                  </Button>
                </div>
              </Form>
            </div>
          )}
        </Modal.Body>
      </Modal>

      {/* Fullscreen Interactive Zoom & Rotate Image Viewer Modal */}
      <Modal 
        show={showImageModal} 
        onHide={handleCloseImageModal} 
        centered
        size="xl"
        className="rx-image-viewer-modal"
        backdropClassName="rx-viewer-backdrop"
      >
        <Modal.Header closeButton className="rx-viewer-header py-2 px-3">
          <div className="d-flex align-items-center gap-2">
            <FaFilePrescription className="text-primary" />
            <span className="fw-bold text-white small">Doctor Prescription Inspector</span>
          </div>
          <div className="rx-viewer-toolbar d-flex align-items-center gap-2 me-3">
            <Button variant="dark" size="sm" onClick={handleZoomOut} title="Zoom Out"><FaSearchMinus /></Button>
            <span className="rx-zoom-indicator small text-muted">{Math.round(zoom * 100)}%</span>
            <Button variant="dark" size="sm" onClick={handleZoomIn} title="Zoom In"><FaSearchPlus /></Button>
            <Button variant="dark" size="sm" onClick={handleRotate} title="Rotate 90°"><FaRedo /></Button>
            <Button variant="dark" size="sm" onClick={handleResetZoom} title="Reset View">Reset</Button>
            {selectedImageUrl && (
              <a href={selectedImageUrl} download target="_blank" rel="noopener noreferrer" className="btn btn-dark btn-sm" title="Open Full in New Tab">
                <FaDownload />
              </a>
            )}
          </div>
        </Modal.Header>
        <Modal.Body className="rx-viewer-body p-0 d-flex align-items-center justify-content-center">
          <div className="rx-viewer-canvas">
            <Image 
              src={selectedImageUrl} 
              alt="Prescription Full Resolution" 
              className="rx-viewer-image"
              style={{
                transform: `scale(${zoom}) rotate(${rotation}deg)`,
                transition: 'transform 0.2s cubic-bezier(0.16, 1, 0.3, 1)'
              }}
            />
          </div>
        </Modal.Body>
      </Modal>
    </Container>
  );
};

export default PrescriptionManagement;